import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { CircleCheck, Handshake } from 'lucide-react'
import { cn } from '@/lib/utils'
import { padNumber } from '@/lib/photos'
import { heartBurst, originOf } from '@/lib/confetti'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'
import { PROMISES } from '@/constants/birthday'

const PromisesSection: FC = (): ReactNode => {
    const [sealed, setSealed] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const last = PROMISES.items.length - 1

    const seal = (): void => {
        if (sealed) return

        setSealed(true)
        void heartBurst(originOf(buttonRef.current))
    }

    return (
        <section id='promises' aria-labelledby='promises-title' className='relative overflow-hidden py-24 sm:py-32'>
            <div className='mx-auto max-w-4xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        id='promises-title'
                        title={PROMISES.title}
                        subtitle={PROMISES.subtitle}
                    />
                </Reveal>

                <ol className='mt-14 grid gap-4 sm:gap-5 md:grid-cols-2'>
                    {PROMISES.items.map((promise, index) => (
                        <li
                            key={promise}
                            className={cn(index === last && 'md:col-span-2 md:mx-auto md:w-[calc(50%-0.625rem)]')}
                        >
                            <Reveal delay={(index % 2) * 100} className='h-full'>
                                <div
                                    className={cn(
                                        'flex h-full items-start gap-4 rounded-3xl border bg-white p-5 shadow-lg shadow-primary/5 transition-colors duration-500 sm:p-6',
                                        sealed ? 'border-primary/40' : 'border-primary/10'
                                    )}
                                >
                                    <span className='font-heading text-4xl font-semibold leading-none text-primary/80 tabular-nums'>
                                        {padNumber(index + 1)}
                                    </span>

                                    <p className='flex-1 pt-1 text-base font-semibold leading-relaxed text-pretty sm:text-lg'>
                                        {promise}
                                    </p>

                                    {sealed && (
                                        <CircleCheck
                                            aria-hidden
                                            className='mt-1 size-5 shrink-0 animate-in zoom-in duration-500 fill-mode-both fill-primary/10 text-primary motion-reduce:animate-none'
                                            style={{ animationDelay: `${index * 90}ms` }}
                                        />
                                    )}
                                </div>
                            </Reveal>
                        </li>
                    ))}
                </ol>

                <div className='mt-12 text-center'>
                    <button
                        ref={buttonRef}
                        type='button'
                        onClick={seal}
                        aria-pressed={sealed}
                        className={cn(
                            'inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-bold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30',
                            sealed
                                ? 'cursor-default bg-wine text-white shadow-lg shadow-wine/25'
                                : 'bg-primary text-primary-foreground shadow-xl shadow-primary/30 hover:-translate-y-0.5 hover:brightness-110'
                        )}
                    >
                        {sealed ? <CircleCheck className='size-5' /> : <Handshake className='size-5' />}
                        {PROMISES.button}
                    </button>
                </div>
            </div>
        </section>
    )
}

export default PromisesSection
